import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import axios from '../../../interceptors/axios';

// ── Thumbnail (auth-gated blob cache) ─────────────────────────────────────────
const thumbCache = new Map<number, string>();

export const ThumbImg: React.FC<{ fileId: number }> = ({ fileId }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(thumbCache.get(fileId) ?? null);
  useEffect(() => {
    if (thumbCache.has(fileId)) return;
    let cancelled = false;
    axios.get(`/media/thumb/${fileId}`, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        const url = URL.createObjectURL(res.data as Blob);
        thumbCache.set(fileId, url);
        setBlobUrl(url);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [fileId]);
  if (!blobUrl) return null;
  return (
    <Box
      component="img"
      src={blobUrl}
      alt=""
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
};

// ── 3D Folder SVG ─────────────────────────────────────────────────────────────
// IDs prefixed with "mc-" to avoid global SVG gradient ID collisions
const IcoFolder: React.FC = () => (
  <svg width={56} height={52} viewBox="0 0 56 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="28" cy="50" rx="18" ry="2.5" fill="rgba(80,70,180,.18)" />
    <rect x="4" y="14" width="48" height="32" rx="4" fill="url(#mc-fback)" />
    <path d="M4 14 C4 14 4 10 8 10 L20 10 C22 10 23 12 24 14 Z" fill="url(#mc-ftab)" />
    <rect x="4" y="17" width="48" height="29" rx="4" fill="url(#mc-ffront)" />
    <rect x="8" y="20" width="40" height="2" rx="1" fill="rgba(255,255,255,.22)" />
    <rect x="4" y="17" width="48" height="4" rx="4" fill="rgba(0,0,0,.06)" />
    <defs>
      <linearGradient id="mc-fback" x1="28" y1="14" x2="28" y2="46" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#7c6ff7" />
        <stop offset="100%" stopColor="#5b52d4" />
      </linearGradient>
      <linearGradient id="mc-ftab" x1="14" y1="10" x2="14" y2="14" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#9d96f9" />
        <stop offset="100%" stopColor="#7c6ff7" />
      </linearGradient>
      <linearGradient id="mc-ffront" x1="28" y1="17" x2="28" y2="46" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#a89cf8" />
        <stop offset="100%" stopColor="#6c63ef" />
      </linearGradient>
    </defs>
  </svg>
);

// ── Glass Checkbox ─────────────────────────────────────────────────────────────
export const GlassCheckbox: React.FC<{ checked: boolean; onChange: () => void }> = ({ checked, onChange }) => (
  <Box
    component="span"
    onClick={e => { e.stopPropagation(); onChange(); }}
    sx={{
      width: 17, height: 17, borderRadius: '4px', flexShrink: 0, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: checked ? 'var(--accent)' : 'var(--glass-bg)',
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      border: `1px solid ${checked ? 'var(--accent)' : 'var(--glass-border)'}`,
      '&:hover': { borderColor: 'var(--glass-border-strong, var(--accent))' },
    }}
  >
    {checked && (
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M2 5l2.5 2.5 3.5-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </Box>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
/** Имя без расширения — расширение показывается отдельным полем и не редактируется. */
export const baseName = (name: string) => {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
};

/** Расширение вместе с точкой, чтобы вернуть его при переименовании. */
export const nameExt = (name: string) => {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot) : '';
};

export const isVideo = (mime: string) => mime.startsWith('video/');

export const fmtMSS = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
};

const availChip = (status?: string) => {
  if (status === 'expired') return { label: 'Expired', color: '#f87171', bg: 'rgba(248,113,113,.1)', dot: '#ef4444' };
  if (status === 'not_started') return { label: 'Not started', color: '#818cf8', bg: 'rgba(129,140,248,.1)', dot: '#6366f1' };
  return { label: 'Active', color: '#4ade80', bg: 'rgba(74,222,128,.1)', dot: '#22c55e' };
};

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CardFolder {
  id: number;
  name: string;
  file_count?: number;
}

export interface CardFile {
  id: number;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  duration_ms: number | null;
}

// ── MediaFolderCard ───────────────────────────────────────────────────────────

export interface MediaFolderCardProps {
  folder: CardFolder;
  onClick?: () => void;
  checked?: boolean;
  onCheck?: () => void;
  selected?: boolean;
  /** Rendered in top-right corner (e.g. action menu) */
  menuSlot?: React.ReactNode;
  /** Drag-and-drop target state */
  isDragTarget?: boolean;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: () => void;
  onDrop?: (e: React.DragEvent) => void;
  /** Folder as drag source */
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
}

export const MediaFolderCard: React.FC<MediaFolderCardProps> = ({
  folder, onClick, checked, onCheck, selected, menuSlot,
  isDragTarget, onDragOver, onDragLeave, onDrop,
  draggable, onDragStart, onDragEnd,
}) => (
  <Box
    draggable={draggable}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    onClick={onClick}
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    sx={{
      aspectRatio: '1',
      borderRadius: '12px',
      overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      position: 'relative',
      background: 'linear-gradient(145deg, var(--folder-card-from) 0%, var(--folder-card-to) 100%)',
      border: `1px solid ${isDragTarget ? 'var(--accent)' : (checked || selected) ? 'var(--accent)' : 'var(--glass-border)'}`,
      outline: isDragTarget ? '2px solid var(--accent)' : (checked || selected) ? '1.5px solid var(--accent)' : 'none',
      outlineOffset: '-1px',
      boxShadow: isDragTarget ? '0 0 0 4px var(--accent-glow)' : 'none',
      transform: isDragTarget ? 'scale(1.04)' : 'none',
      transition: 'transform .15s, box-shadow .15s, border-color .1s, outline .1s',
      '&:hover': { transform: isDragTarget ? 'scale(1.04)' : 'scale(1.02)', boxShadow: isDragTarget ? '0 0 0 4px var(--accent-glow)' : '0 8px 24px var(--accent-glow)' },
    }}
  >
    {/* Checkbox (top-left) */}
    {onCheck && (
      <Box sx={{ position: 'absolute', top: '8px', left: '8px', zIndex: 3 }}>
        <GlassCheckbox checked={!!checked} onChange={onCheck} />
      </Box>
    )}
    {/* Menu slot (top-right) */}
    {menuSlot && (
      <Box sx={{ position: 'absolute', top: '8px', right: '8px', zIndex: 3 }} onClick={e => e.stopPropagation()}>
        {menuSlot}
      </Box>
    )}
    {/* Centered folder icon */}
    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <IcoFolder />
    </Box>
    {/* Bottom gradient overlay */}
    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, px: '10px', pt: '22px', pb: '9px', background: 'linear-gradient(to top, rgba(0,0,0,.75) 0%, transparent 100%)' }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>
        {folder.name}
      </Typography>
      {folder.file_count !== undefined && (
        <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,.6)', mt: '2px' }}>
          {folder.file_count} items
        </Typography>
      )}
    </Box>
  </Box>
);

// ── MediaFileCard ─────────────────────────────────────────────────────────────

export interface MediaFileCardProps {
  file: CardFile;
  onClick?: () => void;
  onDoubleClick?: () => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  selected?: boolean;
  checked?: boolean;
  onCheck?: () => void;
  /** Availability badge (Media section only) */
  avail_status?: string;
  /** Show schedule indicator badge */
  avail_sched_enabled?: boolean;
  /** Show VIDEO/IMAGE type badge top-left (Playlist library mode) */
  showTypeBadge?: boolean;
  /** Show blue "drag / dbl-click" hint overlay on hover */
  showDragHint?: boolean;
  dblClickHint?: string;
  dragHint?: string;
  /** Rendered in top-right corner (e.g. action menu) */
  menuSlot?: React.ReactNode;
  /** Inline rename mode (Media section only) */
  editing?: boolean;
  editingName?: string;
  onEditName?: (v: string) => void;
  onCommitRename?: (id: number, name: string) => void;
  onCancelRename?: () => void;
  isDuplicate?: boolean;
  transcoding?: boolean;
}

export const MediaFileCard: React.FC<MediaFileCardProps> = ({
  file, onClick, onDoubleClick,
  draggable: isDraggable, onDragStart, onDragEnd,
  selected, checked, onCheck,
  avail_status, avail_sched_enabled, showTypeBadge, showDragHint, dblClickHint, dragHint,
  menuSlot,
  editing, editingName, onEditName, onCommitRename, onCancelRename, isDuplicate, transcoding,
}) => {
  const iv = isVideo(file.mime_type);
  const chip = availChip(avail_status);

  return (
    <Box
      draggable={isDraggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      sx={{
        aspectRatio: '1',
        borderRadius: '12px',
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        background: 'linear-gradient(145deg, var(--folder-card-from) 0%, var(--folder-card-to) 100%)',
        border: `1px solid ${(checked || selected) ? 'var(--accent)' : 'var(--glass-border)'}`,
        outline: (checked || selected) ? '1.5px solid var(--accent)' : 'none',
        outlineOffset: '-1px',
        transition: 'transform .15s, box-shadow .15s',
        '&:hover': { transform: 'scale(1.02)', boxShadow: '0 8px 24px rgba(0,0,0,.35)' },
        '&:hover .mc-drag-hint': { opacity: showDragHint ? 1 : 0 },
      }}
    >
      {/* Thumbnail */}
      <ThumbImg fileId={file.id} />

      {/* Play icon overlay for video */}
      {iv && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, pointerEvents: 'none' }}>
          <Box sx={{ color: 'rgba(255,255,255,.25)', display: 'flex' }}>
            <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </Box>
        </Box>
      )}

      {/* Schedule badge (bottom-left) */}
      {avail_sched_enabled && (
        <Box sx={{ position: 'absolute', bottom: '28px', left: '6px', zIndex: 4, width: 18, height: 18, borderRadius: '5px', bgcolor: 'rgba(99,102,241,.85)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Schedule active">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="12" cy="12" r="9" /><line x1="12" y1="7" x2="12" y2="12" /><line x1="12" y1="12" x2="15" y2="14" />
          </svg>
        </Box>
      )}

      {/* Top-left: checkbox (Media) OR type badge (Playlist) */}
      {onCheck ? (
        <Box sx={{ position: 'absolute', top: '8px', left: '8px', zIndex: 3 }}>
          <GlassCheckbox checked={!!checked} onChange={onCheck} />
        </Box>
      ) : showTypeBadge ? (
        <Box sx={{ position: 'absolute', top: '8px', left: '8px', zIndex: 3, px: '6px', py: '2px', borderRadius: '5px', background: iv ? 'rgba(99,102,241,.85)' : 'rgba(16,185,129,.85)', backdropFilter: 'blur(4px)' }}>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#fff', letterSpacing: '.08em', textTransform: 'uppercase', lineHeight: 1.6 }}>
            {iv ? 'video' : 'image'}
          </Typography>
        </Box>
      ) : null}

      {/* Top-right: avail chip and/or menu slot */}
      {(avail_status || menuSlot) && (
        <Box
          sx={{ position: 'absolute', top: '8px', right: '8px', zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}
          onClick={e => e.stopPropagation()}
        >
          {avail_status && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 11, fontWeight: 600, px: '7px', py: '2px', borderRadius: 20, bgcolor: chip.bg, color: chip.color, backdropFilter: 'blur(8px)', border: `1px solid ${chip.color}22` }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: chip.dot }} />
              {chip.label}
            </Box>
          )}
          {menuSlot}
        </Box>
      )}

      {/* Drag hint overlay (Playlist only) */}
      {showDragHint && (
        <Box
          className="mc-drag-hint"
          sx={{ position: 'absolute', inset: 0, zIndex: 4, background: 'rgba(99,102,241,.32)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px', opacity: 0, transition: 'opacity .15s', pointerEvents: 'none' }}
        >
          <Typography sx={{ fontSize: 22, color: 'rgba(255,255,255,.95)', lineHeight: 1, fontWeight: 300 }}>+</Typography>
          {(dblClickHint || dragHint) && (
            <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,.9)', fontWeight: 600, textAlign: 'center', lineHeight: 1.4, px: '8px' }}>
              {dblClickHint}{dblClickHint && dragHint ? <br /> : null}{dragHint}
            </Typography>
          )}
        </Box>
      )}

      {/* Transcoding badge */}
      {transcoding && (
        <Box sx={{ position: 'absolute', top: '8px', left: '8px', zIndex: 3, display: 'flex', alignItems: 'center', gap: '4px', fontSize: 10, fontWeight: 700, px: '7px', py: '3px', borderRadius: 20, bgcolor: 'rgba(99,102,241,.25)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,.4)', backdropFilter: 'blur(8px)' }}>
          <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', border: '1.5px solid #a5b4fc', borderTopColor: 'transparent', animation: 'spin .8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
          Converting...
        </Box>
      )}

      {/* Bottom: editing input OR standard overlay */}
      {editing ? (
        <Box
          sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 5, p: '10px 12px 12px', background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
          onClick={e => e.stopPropagation()}
        >
          <Box
            component="input"
            autoFocus
            value={editingName ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onEditName?.(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => {
              e.stopPropagation();
              if (e.key === 'Enter' && !isDuplicate) onCommitRename?.(file.id, editingName ?? '');
              if (e.key === 'Escape') onCancelRename?.();
            }}
            onBlur={() => onCancelRename?.()}
            sx={{ width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', borderBottom: `1.5px solid ${isDuplicate ? '#f87171' : 'var(--accent)'}`, color: '#fff', fontSize: 13, fontWeight: 500, outline: 'none', pb: '2px' }}
          />
          {isDuplicate
            ? <Typography sx={{ fontSize: 10, color: '#f87171', mt: '3px' }}>Already exists</Typography>
            : <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,.5)', mt: '3px' }}>Enter — save · Esc — cancel</Typography>
          }
        </Box>
      ) : (
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2, px: '9px', pt: '18px', pb: '8px', background: 'linear-gradient(to top, rgba(0,0,0,.96) 0%, rgba(0,0,0,.7) 50%, rgba(0,0,0,.2) 80%, transparent 100%)' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3, mb: '4px' }}>
            {baseName(file.original_name)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,.75)' }}>
              {file.size_bytes > 0 ? `${(file.size_bytes / 1024 / 1024).toFixed(2)} MB` : '—'}
            </Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,.75)' }}>
              {fmtMSS(file.duration_ms ?? 10000)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
